import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { GAME_ASSET_SLUG } from "@/config/game";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { Popover } from "@/design-system/Popover/Popover";
import { CoinIcon } from "@/designSystemIcons";
import { useStartClubImport } from "@/features/MyClub/EmptyClub/useStartClubImport";
import { cn } from "@/lib/utils";
import { FavoritesSheet } from "@/pages/FrontPage/ForYou/FavoritesSheet";
import { GalleryTokenIcon } from "@/pages/Gallery/components/GallerySetCard";
import {
  initFavoritesFlow,
  setPickerType,
} from "@/redux/frontPage/frontPageSlice";
import { loadedContentSelector } from "@/redux/loadedContent/loadedContentSlice";
import { AppDispatch } from "@/redux/store";
import {
  userFavoriteClubIdsSelector,
  userFavoriteCountryIdSelector,
} from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { ClubStats, clubStatsSelector } from "@repo/shared/redux";
import { formatPrice, formatTime } from "@repo/shared/utils";
import {
  ChevronDown,
  CircleCheck,
  Flag,
  House,
  Info,
  RefreshCw,
  Settings2,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { ComponentType, ReactNode, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { SetupDialog } from "./SetupDialog";

export const clubCrestUrl = (clubBadgeId?: number) =>
  clubBadgeId
    ? `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/${GAME_ASSET_SLUG}/clubs/${clubBadgeId}.png`
    : undefined;

const compact = (value: number) => formatPrice({ price: value, compact: true });

/** Animates a label away as the rail collapses, leaving only the icon. */
const RailLabel = ({
  collapsed,
  className,
  children,
}: {
  collapsed: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "flex min-w-0 flex-1 items-center gap-2 overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out",
      collapsed ? "max-w-0 opacity-0" : "max-w-52 opacity-100",
      className,
    )}
  >
    {children}
  </div>
);

type NavItem = {
  label: string;
  to: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  count?: string;
  /** Before the count, saying what it counts, e.g. item score's diamond. */
  countIcon?: ReactNode;
  /** Matches only the exact path, so Overview isn't lit on every sub-route. */
  end?: boolean;
  /** Lit by more than the path, e.g. a sort in the query. */
  isActive?: (location: { pathname: string; search: string }) => boolean;
  /** A view of the row above: indented behind a rule. */
  sub?: boolean;
};

const NavRow = ({
  item,
  collapsed,
  primary,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  primary?: boolean;
  onNavigate?: () => void;
}) => {
  const location = useLocation();
  const Icon = item.icon;
  // A sub-row has no icon to stand for it in the collapsed rail.
  if (item.sub && collapsed) return null;
  // Hash links point at sections of the home page; NavLink would light them
  // all on /my-club, so they are never shown as the current page.
  const isHash = item.to.includes("#");

  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      onClick={onNavigate}
      state={{ from: location.pathname }}
      className={({ isActive }) =>
        cn(
          "flex h-11 items-center gap-3 rounded-lg px-2.5 transition-colors mobile:h-8",
          item.sub && "ml-4.5 rounded-l-none border-l border-neutral-700 pl-3",
          (item.isActive ? item.isActive(location) : isActive) && !isHash
            ? "bg-neutral-800 text-neutral-50"
            : "text-neutral-400 hover:bg-streamline-row-alt hover:text-neutral-200",
        )
      }
    >
      <span className="flex size-4 flex-none items-center justify-center">
        <Icon className="size-4" strokeWidth={1.8} />
      </span>
      <RailLabel collapsed={collapsed}>
        <span className={cn(primary ? "text-sm" : "text-sm")}>
          {item.label}
        </span>
        {item.count && (
          <span className="ml-auto flex items-center gap-1 text-xs text-neutral-500 tabular-nums">
            {item.countIcon}
            {item.count}
          </span>
        )}
      </RailLabel>
    </NavLink>
  );
};

/** A row like NavRow that opens something here rather than going anywhere. */
const ActionRow = ({
  label,
  icon: Icon,
  trailing,
  collapsed,
  onClick,
  active = false,
}: {
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  /** Right side: the current pick, e.g. a crest or a flag. */
  trailing?: ReactNode;
  collapsed: boolean;
  onClick: () => void;
  /** Lit like a NavRow on the page it leads to. */
  active?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    title={collapsed ? label : undefined}
    aria-current={active ? "page" : undefined}
    className={cn(
      "flex h-11 items-center gap-3 rounded-lg px-2.5 text-left transition-colors mobile:h-8",
      active
        ? "bg-neutral-800 text-neutral-50"
        : "text-neutral-400 hover:bg-streamline-row-alt hover:text-neutral-200",
    )}
  >
    <span className="flex size-4 flex-none items-center justify-center">
      <Icon className="size-4" strokeWidth={1.8} />
    </span>
    <RailLabel collapsed={collapsed}>
      <span className="text-sm">{label}</span>
      {trailing && (
        <span className="ml-auto flex items-center gap-1">{trailing}</span>
      )}
    </RailLabel>
  </button>
);

const SBC_ICON = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/sbc-tmp.png`;

const assetUrl = (kind: "clubs" | "countries", id: number) =>
  `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/${GAME_ASSET_SLUG}/${kind}/${id}.png?scale=50`;

/**
 * Extra: the account settings and the favourites My Club builds on. A
 * favourite, once picked, shows on its row.
 */
const ExtraRows = ({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) => {
  const { t } = useTranslation("myClub");
  const dispatch = useDispatch<AppDispatch>();
  const favoriteClubIds = useSelector(userFavoriteClubIdsSelector);
  const favoriteCountryId = useSelector(userFavoriteCountryIdSelector);
  const { clubs, countries } = useSelector(loadedContentSelector);
  const [setupOpen, setSetupOpen] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const country = countries?.find((c) => c.id === favoriteCountryId);
  /* With the favourite picked, its row opens My Club's page for it; without,
     the picker, to choose one. */
  const openFavorite = (type: "club" | "country") => {
    const picked =
      type === "club" ? favoriteClubIds.length > 0 : favoriteCountryId != null;
    if (!picked) return openFavorites(type);
    onNavigate?.();
    navigate(`/my-club/favorite-${type}`);
  };
  const openFavorites = (type: "club" | "country") => {
    dispatch(
      initFavoritesFlow({
        clubIds: favoriteClubIds,
        countryId: favoriteCountryId ?? null,
      }),
    );
    dispatch(setPickerType(type));
    onNavigate?.();
    setFavoritesOpen(true);
  };

  return (
    <>
      <GroupLabel collapsed={collapsed}>{t("nav.extra")}</GroupLabel>
      <ActionRow
        label={t(
          favoriteClubIds.length > 1 ? "nav.favoriteClubs" : "nav.favoriteClub",
        )}
        icon={Star}
        collapsed={collapsed}
        onClick={() => openFavorite("club")}
        active={pathname === "/my-club/favorite-club"}
        trailing={favoriteClubIds.map((id) => (
          <img
            key={id}
            src={assetUrl("clubs", id)}
            alt={clubs?.find((c) => c.id === id)?.name ?? ""}
            title={clubs?.find((c) => c.id === id)?.name}
            className="size-4 object-contain"
          />
        ))}
      />
      <ActionRow
        label={t("nav.favoriteCountry")}
        icon={Flag}
        collapsed={collapsed}
        onClick={() => openFavorite("country")}
        active={pathname === "/my-club/favorite-country"}
        trailing={
          favoriteCountryId != null && (
            <img
              src={assetUrl("countries", favoriteCountryId)}
              alt={country?.name ?? ""}
              title={country?.name}
              className="h-3 w-4 object-contain"
            />
          )
        }
      />
      <ActionRow
        label={t("nav.setup")}
        icon={Settings2}
        collapsed={collapsed}
        onClick={() => {
          onNavigate?.();
          setSetupOpen(true);
        }}
      />
      {/* Portalled: the panel is its own stacking context, so a dialog
          rendered inside it would sit under the pitch. */}
      {createPortal(
        <>
          <SetupDialog isOpen={setupOpen} onClose={() => setSetupOpen(false)} />
          <FavoritesSheet
            isOpen={favoritesOpen}
            onClose={() => setFavoritesOpen(false)}
          />
        </>,
        document.body,
      )}
    </>
  );
};

const GroupLabel = ({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: ReactNode;
}) => (
  <RailLabel collapsed={collapsed} className="px-2.5 pt-2.5 pb-1.5">
    <span className="text-xxs tracking-widest text-neutral-500 uppercase">
      {children}
    </span>
  </RailLabel>
);

/**
 * A figure's label with an info icon, saying what goes into the figure. With
 * hover it's a tooltip, lifted by z-120 over the popover (z-110) it sits in.
 * Without, a tap opens it as a popover like the app's other info icons: a
 * tooltip on a phone covers the rows below it and won't close again.
 */
const Explained = ({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children: ReactNode;
}) => {
  const canHover = useMediaQuery("(hover: hover)");
  const icon = (
    <Info className="size-3 flex-none text-foreground-muted" strokeWidth={2} />
  );

  if (!canHover)
    return (
      <Popover
        placement="bottom"
        align="start"
        width={260}
        newDesignSystem
        negLabel=""
        posLabel=""
        title={title}
        trigger={
          <button
            type="button"
            // Taller than the text, for a thumb.
            className="-my-1.5 flex items-center gap-1 py-1.5 text-left"
          >
            {children}
            {icon}
          </button>
        }
      >
        <p>{body}</p>
      </Popover>
    );

  return (
    <Tooltip
      content={
        <div className="flex w-60 flex-col gap-1 p-1 text-xs">
          <span className="font-semibold text-neutral-50">{title}</span>
          <span className="text-neutral-300">{body}</span>
        </div>
      }
      side="bottom"
      delayDuration={50}
      className="z-[120]"
      triggerClassName="cursor-default items-center gap-1"
    >
      {children}
      {icon}
    </Tooltip>
  );
};

/** Every club figure the old stat bar showed, behind the club value. */
export const ClubValuePopover = ({
  stats,
  stacked = false,
}: {
  stats: ClubStats;
  /** The label over the value, the chevron beside both: a tight header row. */
  stacked?: boolean;
}) => {
  const { t } = useTranslation("myClub");
  const rows = [
    { label: t("bar.coins"), value: stats.coins, coin: true, strong: true },
    {
      label: t("bar.clubValue"),
      value: stats.clubValue,
      coin: true,
      strong: true,
      info: {
        title: t("bar.tooltip.clubValue.title"),
        body: t("bar.tooltip.clubValue.body"),
      },
    },
    {
      label: t("bar.potentialCoins"),
      value: stats.potentialCoins,
      coin: true,
      info: {
        title: t("bar.tooltip.potentialCoins.title"),
        body: t("bar.tooltip.potentialCoins.body"),
      },
    },
    {
      label: t("bar.sbcFodder"),
      value: stats.sbcFodder,
      coin: false,
      itemScore: true,
      info: {
        title: t("bar.tooltip.sbcFodder.title"),
        body: t("bar.tooltip.sbcFodder.body"),
      },
    },
    { label: t("bar.transferList"), value: stats.transferList, coin: true },
    // No balance until the extension sends one: no row rather than a 0.
    ...(stats.galleryTokens != null
      ? [
          {
            label: t("bar.galleryTokens"),
            value: stats.galleryTokens,
            galleryToken: true,
          },
        ]
      : []),
  ];

  return (
    <Popover
      placement="bottom"
      align="start"
      width={250}
      newDesignSystem
      negLabel=""
      posLabel=""
      trigger={
        <span className="flex items-center gap-1.5 text-xs leading-tight text-neutral-400 hover:text-neutral-200">
          <span
            className={cn(
              "flex",
              stacked ? "flex-col items-start gap-0.5" : "items-center gap-1.5",
            )}
          >
            <span className="whitespace-nowrap">{t("bar.clubValue")}</span>
            <span className="flex items-center gap-1 font-oswald text-sm font-medium text-neutral-300 tabular-nums">
              <CoinIcon size={13} />
              {compact(stats.clubValue ?? 0)}
            </span>
          </span>
          <ChevronDown className="size-3.5" strokeWidth={2.4} />
        </span>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5 border-b border-neutral-750 pb-3">
          <img
            src={clubCrestUrl(stats.clubBadgeId)}
            alt=""
            className="size-7 object-contain"
          />
          <div className="flex flex-col gap-1">
            <span className="text-sm leading-none font-semibold">
              {stats.clubName}
            </span>
            <Explained
              title={t("bar.tooltip.clubRank.title")}
              body={t("bar.tooltip.clubRank.body")}
            >
              <span className="text-xs leading-none text-foreground-muted">
                {t("bar.clubRank")}{" "}
                <span className="font-semibold text-orange-400">
                  #{(stats.rank ?? 0).toLocaleString("en-US")}
                </span>
              </span>
            </Explained>
          </div>
        </div>
        {rows.map((row) => (
          <div key={row.label} className="flex items-center gap-2.5">
            {row.info ? (
              <Explained {...row.info}>
                <span className="text-xxs tracking-wider text-foreground-muted uppercase">
                  {row.label}
                </span>
              </Explained>
            ) : (
              <span className="text-xxs tracking-wider text-foreground-muted uppercase">
                {row.label}
              </span>
            )}
            <span
              className={cn(
                "ml-auto flex items-center gap-1 font-oswald text-base leading-none font-medium tabular-nums",
                row.strong ? "text-neutral-50" : "text-neutral-300",
              )}
            >
              {row.coin && <CoinIcon size={12} />}
              {row.itemScore && <ItemScoreDiamond size={12} />}
              {row.galleryToken && <GalleryTokenIcon className="size-3" />}
              <span>{(row.value ?? 0).toLocaleString("en-US")}</span>
            </span>
          </div>
        ))}
      </div>
    </Popover>
  );
};

/**
 * My Club's own navigation: the club, every place in it, and the re-import.
 * Rendered as a collapsible rail on desktop and inside a drawer on mobile, so
 * it takes `collapsed` rather than owning that state.
 */
export const MyClubSidePanel = ({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  /** Called after any link is followed, e.g. to close the mobile drawer. */
  onNavigate?: () => void;
}) => {
  const { t } = useTranslation("myClub");
  const stats = useSelector(clubStatsSelector);
  const startClubImport = useStartClubImport({
    section: "My Club",
    subsection: "Overview",
  });

  const main: NavItem[] = [
    { label: t("nav.overview"), to: "/my-club", icon: House, end: true },
    {
      label: t("nav.playerRecommendations"),
      to: "/my-club/recommendations",
      icon: TrendingUp,
    },
    // Hidden: For your club already sits at the top of the overview.
    // { label: t("nav.getStarted"), to: "/my-club#try-next", icon: CircleCheck },
  ];
  const club: NavItem[] = [
    /*
      My Players, and under it Hidden Profit: the same list sorted by profit.
      The query decides which of the two is lit.
    */
    {
      label: t("nav.myPlayers"),
      to: "/my-club/players",
      icon: Users,
      count: (stats.playersCount ?? 0).toLocaleString("en-US"),
      isActive: ({ pathname, search }) =>
        pathname === "/my-club/players" && !search.includes("sort=profit"),
    },
    {
      label: t("section.hiddenProfit"),
      to: "/my-club/players?sort=profit",
      icon: () => <CoinIcon size={15} />,
      sub: true,
      isActive: ({ pathname, search }) =>
        pathname === "/my-club/players" && search.includes("sort=profit"),
    },
    // Hidden until My Evolutions moves under My Club.
    // {
    //   label: t("section.myEvolutions"),
    //   to: "/evolutions/my-evolutions/players",
    //   icon: Sparkles,
    //   count: (stats.evolvedPlayersCount ?? 0).toLocaleString("en-US"),
    // },
    {
      label: t("nav.sbcFodder"),
      to: "/my-club/sbc-fodder-breakdown",
      // The SBC art for the row; the fodder is counted in item score.
      icon: () => (
        <img src={SBC_ICON} alt="" className="size-4 object-contain" />
      ),
      count: compact(stats.sbcFodder ?? 0),
      countIcon: <ItemScoreDiamond size={12} />,
    },
  ];
  // Tools are hidden for now; their destinations aren't settled.
  // const tools: NavItem[] = [
  //   {
  //     label: t("nav.clubSquadBuilder"),
  //     to: "/squad-builder?squad=active",
  //     icon: LayoutGrid,
  //   },
  //   {
  //     label: t("nav.evolutionChains"),
  //     to: "/evolutions/my-evolutions/players",
  //     icon: GitBranch,
  //   },
  // ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-15 flex-none items-center gap-2.5 border-b border-neutral-800 px-3.5">
        <img
          src={clubCrestUrl(stats.clubBadgeId)}
          alt=""
          className="size-7 flex-none object-contain"
        />
        <RailLabel
          collapsed={collapsed}
          className="flex-col items-start gap-0.5"
        >
          <span className="text-sm font-semibold">{stats.clubName}</span>
          <ClubValuePopover stats={stats} />
        </RailLabel>
      </div>

      <nav className="flex flex-col gap-0.5 overflow-y-auto px-2 py-2.5">
        {main.map((item) => (
          <NavRow
            key={item.label}
            item={item}
            collapsed={collapsed}
            primary
            onNavigate={onNavigate}
          />
        ))}
        <GroupLabel collapsed={collapsed}>{t("nav.yourClub")}</GroupLabel>
        {club.map((item) => (
          <NavRow
            key={item.label}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
        <ExtraRows collapsed={collapsed} onNavigate={onNavigate} />
        {/*
        <GroupLabel collapsed={collapsed}>{t("nav.tools")}</GroupLabel>
        {tools.map((item) => (
          <NavRow
            key={item.label}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
        */}
      </nav>

      <button
        onClick={() => {
          onNavigate?.();
          startClubImport();
        }}
        title={collapsed ? t("nav.reimport") : undefined}
        className="mt-auto flex flex-none items-center gap-2.5 border-t border-neutral-800 px-3.5 py-3 text-left text-neutral-400 hover:text-neutral-200"
      >
        <RefreshCw className="size-4 flex-none" strokeWidth={1.8} />
        <RailLabel
          collapsed={collapsed}
          className="flex-col items-start gap-0.5"
        >
          <span className="text-xs font-semibold text-neutral-300">
            {t("nav.reimport")}
          </span>
          {stats.lastImportedAt > 0 && (
            <span className="text-xs text-neutral-500">
              {t("home.imported", {
                time: formatTime(Date.now() - stats.lastImportedAt),
              })}
            </span>
          )}
        </RailLabel>
      </button>
    </div>
  );
};
